import React, { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../api';
import { useAuth } from '../context/AuthContext';
import ProductPickerPopover from '../components/ProductPickerPopover';
import { ReceivingLineEditor, ReceivingLineSummary, ProgressBar, deliverySubtitle } from '../components/ProcurementReceiving';

/**
 * Вкладка флориста «Приёмка» (04.11.2026, только при закупке с биржи — пункт меню с flag).
 * Кнопки «Пришла» нет: поставка из биржи открыта для приёмки всем флористам в день ожидания и
 * дальше. Список поставок → строки («○ не принята — можно взять» / «✓ …») → экран строки →
 * «Строка принята · к списку» — и обратно к СЛЕДУЮЩЕЙ непринятой строке. Несколько флористов
 * принимают одну поставку одновременно: строку, которую уже сохранил другой, сервер вернёт
 * свежей (409), экран её перечитает. Ниже — принятые поставки, где можно поставить «Стойкость в
 * вазе» (с дня поставки + 5). Телефон 390 px — главный экран (владелец 04.11).
 */
const ddmm = (iso) => { if (!iso) return '—'; const [, m, d] = iso.slice(0, 10).split('-'); return `${d}.${m}`; };
const card = { background: '#fff', border: '1px solid #EBEDEC', '--ch': '8px', '--stroke': '#EBEDEC', padding: 14, cursor: 'pointer' };

function DeliveryHead({ inv, big = false }) {
  return (
    <div>
      <div style={{ fontWeight: big ? 700 : 600, fontSize: big ? 17 : 15, color: '#1C3D2E' }}>{inv.display_title || inv.title}</div>
      <div style={{ fontSize: 13, color: '#5E6561', marginTop: 3 }}>{deliverySubtitle(inv)}</div>
      <ProgressBar done={inv.lines_accepted || 0} total={inv.lines_total || 0} />
    </div>
  );
}

export default function ReceivingPage({ showToast }) {
  const { user } = useAuth();
  const me = user?.name;
  const [list, setList] = useState(null);
  const [invoiceId, setInvoiceId] = useState(null);
  const [invoice, setInvoice] = useState(null);
  const [line, setLine] = useState(null);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState(null);       // 'open' | 'all'; null — ещё не выбран (по умолчанию ниже)
  const scrollTo = useRef(null);                     // id строки, к которой вернуться после «Строка принята»
  const [extraOpen, setExtraOpen] = useState(false);   // «+ Лишнее»: пришло то, чего не заказывали
  const extraRef = useRef(null);
  // Весь каталог для «лишнего» — тем же списком, что у лупы строки (флористу /products/all закрыт)
  const loadExtraOptions = useCallback(() => (invoice && invoice.lines.length
    ? api.getReceivingProductOptions(invoice.lines[0].id) : Promise.resolve({ similar: [], products: [] })), [invoice]);
  const closeExtra = useCallback(() => setExtraOpen(false), []);
  const addExtra = async (p) => {
    setExtraOpen(false);
    try {
      const res = await api.addReceivingExtra(invoice.id, p.id, 1);
      await loadInvoice(invoice.id);
      setLine(res.line);   // сразу на экран строки: сколько пришло
    } catch (e) {
      showToast(e.message || 'Не добавилось', 'error');
    }
  };

  const loadList = useCallback(() => {
    setError(null);
    api.getReceivingInvoices().then(setList).catch(e => setError(e.message || 'Не загрузилось'));
  }, []);
  const loadInvoice = useCallback((id) => {
    setError(null);
    return api.getReceivingInvoice(id).then(setInvoice).catch(e => { setError(e.message || 'Не загрузилось'); setInvoiceId(null); });
  }, []);
  useEffect(() => { loadList(); }, [loadList]);
  useEffect(() => {
    if (invoiceId) loadInvoice(invoiceId);
    else { setInvoice(null); setFilter(null); }
  }, [invoiceId, loadInvoice]);
  // Ушли со списка строк (к поставкам или на экран строки) — «+ Лишнее» не остаётся открытым (08.11)
  useEffect(() => { if (!invoiceId || line) setExtraOpen(false); }, [invoiceId, line]);
  // Строки принимают несколько человек — список строк освежаем раз в 30 с, пока он на экране
  useEffect(() => {
    if (!invoiceId || line) return undefined;
    const t = setInterval(() => loadInvoice(invoiceId), 30000);
    return () => clearInterval(t);
  }, [invoiceId, line, loadInvoice]);
  // По умолчанию «Не принятые», как только принята хоть одна строка
  useEffect(() => {
    if (invoice && filter === null) setFilter(invoice.lines_accepted > 0 && invoice.status !== 'accepted' ? 'open' : 'all');
  }, [invoice, filter]);
  // После «Строка принята» — к следующей непринятой строке, а не в начало списка
  useEffect(() => {
    if (line || !invoice || !scrollTo.current) return;
    const el = document.getElementById(`rline-${scrollTo.current}`);
    scrollTo.current = null;
    if (el) el.scrollIntoView({ block: 'center' });
    else window.scrollTo(0, 0);
  }, [line, invoice, filter]);

  const afterSave = (saved) => {
    const order = invoice.lines.filter(l => !l.cancelled);
    const at = order.findIndex(l => l.id === saved.id);
    const rest = [...order.slice(at + 1), ...order.slice(0, Math.max(0, at))];
    const next = rest.find(l => !l.accepted && l.id !== saved.id);
    scrollTo.current = next ? next.id : null;
    showToast('Строка принята');
    setLine(null);
    loadInvoice(invoice.id);
  };

  if (error) {
    return (
      <div style={{ padding: 16, color: '#B0603C' }}>
        {error} <button type="button" className="btn-link" onClick={() => (invoiceId ? loadInvoice(invoiceId) : loadList())}>Повторить</button>
      </div>
    );
  }

  if (line && invoice) {
    return (
      <ReceivingLineEditor line={line} invoice={invoice} showToast={showToast} fixedBar
        onBack={() => { scrollTo.current = line.id; setLine(null); }} onSaved={afterSave} />
    );
  }

  if (invoiceId) {
    if (!invoice) return <div className="loading"><div className="spinner"></div></div>;
    const active = invoice.lines.filter(l => !l.cancelled);
    const shown = filter === 'open' ? invoice.lines.filter(l => !l.accepted && !l.cancelled) : invoice.lines;
    const vaseFrom = invoice.vase_open ? null : invoice.vase_from;
    const done = invoice.status !== 'accepted' && invoice.lines_total > 0 && invoice.lines_accepted === invoice.lines_total;
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, paddingBottom: 24 }}>
        <button type="button" className="btn-link" onClick={() => { setInvoiceId(null); loadList(); }} style={{ alignSelf: 'flex-start', minHeight: 44 }}>← К поставкам</button>
        <DeliveryHead inv={invoice} big />
        {/* Прогресс и фильтр — прилипают сверху, пока листают строки */}
        <div style={{ position: 'sticky', top: 0, zIndex: 5, background: 'var(--bg-color, #F4F4EF)', padding: '8px 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: '#1C3D2E' }}>
            Принято {invoice.lines_accepted} из {invoice.lines_total}
            {invoice.lines_with_defect > 0 && <span style={{ color: '#B0603C' }}> · брак в {invoice.lines_with_defect}</span>}
          </span>
          <div className="seg" role="tablist">
            <button type="button" role="tab" aria-selected={filter === 'open'} className={`seg-btn${filter === 'open' ? ' on' : ''}`}
              style={{ minHeight: 36 }} onClick={() => setFilter('open')}>Не принятые</button>
            <button type="button" role="tab" aria-selected={filter !== 'open'} className={`seg-btn${filter !== 'open' ? ' on' : ''}`}
              style={{ minHeight: 36 }} onClick={() => setFilter('all')}>Все</button>
          </div>
        </div>
        {active.length === 0 && <div style={{ color: '#999', padding: 24, textAlign: 'center' }}>Строк нет</div>}
        {filter === 'open' && shown.length === 0 && active.length > 0 && (
          <div style={{ color: '#5E6561', padding: 16, textAlign: 'center' }}>Все строки приняты</div>
        )}
        {shown.map(l => <ReceivingLineSummary key={l.id} line={l} onOpen={setLine} vaseFrom={vaseFrom} me={me} />)}
        {invoice.status !== 'accepted' && (
          <>
            <button ref={extraRef} type="button" className="btn btn-secondary" style={{ minHeight: 44 }} onClick={() => setExtraOpen(o => !o)}>
              + Лишнее — пришло то, чего не заказывали
            </button>
            {extraOpen && <ProductPickerPopover anchorRef={extraRef} loadOptions={loadExtraOptions} onClose={closeExtra} title="Лишнее — не заказывали" onSelect={addExtra} />}
          </>
        )}
        {done && (
          <div style={{ padding: 12, background: '#DFF2E8', color: '#1C3D2E', fontSize: 14, '--ch': '6px' }}>
            Все строки приняты — на склад поставку зачислит администратор
          </div>
        )}
      </div>
    );
  }

  if (!list) return <div className="loading"><div className="spinner"></div></div>;
  const item = (inv, vase) => (
    <div key={inv.id} role="button" tabIndex={0} style={card} onClick={() => setInvoiceId(inv.id)}
      onKeyDown={e => { if (e.key === 'Enter') setInvoiceId(inv.id); }}>
      {vase ? (
        <>
          <div style={{ fontWeight: 600, fontSize: 15, color: '#1C3D2E' }}>{inv.display_title || inv.title}</div>
          <div style={{ fontSize: 13, color: '#5E6561', marginTop: 4 }}>оценить в вазе: {inv.vase_missing} · с {ddmm(inv.vase_from)}</div>
        </>
      ) : <DeliveryHead inv={inv} />}
    </div>
  );
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, paddingBottom: 24 }}>
      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.8, color: '#909894', textTransform: 'uppercase' }}>Принимаем</div>
      {list.receiving.length === 0 && !(list.preparing || []).length
        ? <div style={{ color: '#5E6561', padding: '16px 0' }}>Сегодня поставок нет — приёмка откроется в день поставки.</div>
        : list.receiving.map(inv => item(inv, false))}
      {/* Черновик с биржи: пришла или придёт сегодня, но товары ещё не размечены — видно, открыть нельзя (04.11) */}
      {(list.preparing || []).map(inv => (
        <div key={`p${inv.id}`} aria-disabled="true" style={{ ...card, cursor: 'default', opacity: 0.6 }}>
          <div style={{ fontWeight: 600, fontSize: 15, color: '#1C3D2E' }}>{inv.display_title || inv.title}</div>
          <div style={{ fontSize: 13, color: '#5E6561', marginTop: 4 }}>
            готовит администратор — не размечено строк: {inv.unmapped}. Принять можно будет после разметки
          </div>
        </div>
      ))}
      {list.vase.length > 0 && (
        <>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.8, color: '#909894', textTransform: 'uppercase', marginTop: 12 }}>Стойкость в вазе</div>
          {list.vase.map(inv => item(inv, true))}
        </>
      )}
    </div>
  );
}
